import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import i18n from '@/i18n'
import { createMockAdapter } from '@/api/mock'
import type { ApiErrorBody, AuthPayload, Item } from '@/types/api'
import { taskErrorMessage } from '@/utils/taskErrorMessage'
import { systemMessage } from '@/i18n/systemMessage'
import { displayLocale } from '@/i18n/localeState'

const options = {
  baseURL: '/api/v1',
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
}
const http = axios.create(import.meta.env.VITE_MOCK_API === 'true' ? { ...options, adapter: createMockAdapter() } : options)
let accessToken: string | null = null
let refreshPromise: Promise<string> | null = null

export function setAccessToken(token: string | null) { accessToken = token }
export function getAccessToken() { return accessToken }

export function normalizeRequestContentType(config: InternalAxiosRequestConfig) {
  if (typeof FormData === 'undefined' || !(config.data instanceof FormData)) return
  const headers = config.headers as any
  if (!headers) return
  if (typeof headers.delete === 'function') {
    headers.delete('Content-Type')
    headers.delete('content-type')
    return
  }
  delete headers['Content-Type']
  delete headers['content-type']
}

http.interceptors.request.use((config) => {
  normalizeRequestContentType(config)
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
  return config
})

interface RetryConfig extends InternalAxiosRequestConfig { _retried?: boolean }
function requireLogin(ipDenied = false) {
  setAccessToken(null)
  localStorage.removeItem('dcim.refresh_token')
  if (window.location.pathname !== '/login') window.location.assign(`/login?${ipDenied ? 'ip_denied=1&' : ''}redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)
}
http.interceptors.response.use(undefined, async (error: AxiosError<ApiErrorBody>) => {
  const config = error.config as RetryConfig | undefined
  const refreshToken = localStorage.getItem('dcim.refresh_token')
  if (error.response?.data?.error?.code === 'LOGIN_IP_DENIED') {
    requireLogin(true)
    return Promise.reject(error)
  }
  if (error.response?.data?.error?.code === 'MFA_SESSION_REQUIRED') {
    requireLogin()
    return Promise.reject(error)
  }
  if (error.response?.status === 401 && config && !config._retried && refreshToken && !config.url?.includes('/auth/')) {
    config._retried = true
    refreshPromise ??= axios.post<Item<AuthPayload>>('/api/v1/auth/refresh', { refresh_token: refreshToken })
      .then(({ data }) => {
        setAccessToken(data.data.access_token)
        localStorage.setItem('dcim.refresh_token', data.data.refresh_token)
        return data.data.access_token
      }).catch((refreshError) => {
        if (axios.isAxiosError<ApiErrorBody>(refreshError) && refreshError.response?.data?.error?.code === 'LOGIN_IP_DENIED') requireLogin(true)
        if (axios.isAxiosError(refreshError) && refreshError.response?.status === 401) requireLogin()
        throw refreshError
      }).finally(() => { refreshPromise = null })
    const token = await refreshPromise
    config.headers.Authorization = `Bearer ${token}`
    return http(config)
  }
  return Promise.reject(error)
})

export function apiErrorMessage(error: unknown): string {
  if (!axios.isAxiosError<ApiErrorBody>(error)) return i18n.global.t('error.default')
  const code = error.response?.data?.error?.code
  const message = code && i18n.global.te(`error.${code}`) ? i18n.global.t(`error.${code}`) : (error.response?.data?.error?.message || i18n.global.t('error.default'))
  const reason = error.response?.data?.error?.details?.reason
  const combined = typeof reason === 'string' && reason.trim() ? `${message}：${reason.trim()}` : message
  // Preserve combined IPMI/Redfish classification and its side-effect evidence.
  const explained = taskErrorMessage(combined)
  if (displayLocale.value !== 'en-US' || explained !== combined.trim()) return explained
  return typeof reason === 'string' && reason.trim()
    ? `${systemMessage(message)}: ${systemMessage(reason.trim())}`
    : systemMessage(message)
}

export default http
